// Module-level singleton for "is recovery mode active". Exists because
// useSession() is a hook with per-call state, but recoveryMode is a
// global concern (one user, one recovery flow at a time) that needs
// to be visible to every component that calls useSession — not just
// the first one to mount and consume the captured tokens.
//
// Plain pub-sub instead of React Context because it's smaller, doesn't
// require a provider in the tree, and the API surface here is tiny
// (one boolean). Any useSession call subscribes on mount; whoever
// runs the URL bridge writes via setRecoveryActive; everyone reads
// the same value.

type Subscriber = (active: boolean) => void;

let active = false;
const subscribers = new Set<Subscriber>();

export function getRecoveryActive(): boolean {
  return active;
}

/** Idempotent — no-op if the value is already current. Notifies all
 *  subscribers when it changes. */
export function setRecoveryActive(next: boolean): void {
  if (active === next) return;
  active = next;
  for (const fn of subscribers) fn(next);
}

/** Returns an unsubscribe function. Subscribers are NOT called with
 *  the current value on subscribe — read it via getRecoveryActive
 *  in your effect setup if you need the initial value. */
export function subscribeRecoveryActive(fn: Subscriber): () => void {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}
